/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { List, SegmentedControl, Text, TextInput, UnstyledButton } from '@/shared/ui';
import { AdaptiveFormShell } from '@/shared/ui/components/form';
import { DateField } from '@/shared/ui/components/form/inputs';
import { moneyInputMask, moneyMask } from '@/shared/utils/input-masks';
import { CreateLeaseSubmitButton } from './create-lease/SubmitButton';
import { moneyToCents } from './create-lease/masks';
import {
  formatAccrualPeriod,
  formatCents,
  formatIsoDate,
  resolveSituation,
} from '../utils/lease-detail-derivations';
import type { AccrualPeriodActions } from '../hooks/useAccrualPeriodActions';
import type { LeaseExpectation, LeaseReceipt } from '../types/lease.types';

/** What the dialog is for, decided by the month's situation. */
export type AccrualPeriodDialogMode = 'confirm' | 'pay' | 'history';

type PayKind = 'payment' | 'writeOff';

export interface LeaseAccrualPeriodDialogProps {
  expectation: LeaseExpectation | null;
  actions: AccrualPeriodActions;
  onClose: () => void;
}

/**
 * The mode a month opens in: a pro-rata waits for its amount, a month with
 * something owed takes a payment, and a closed month only shows its history.
 *
 * @param expectation - The month the broker clicked.
 * @returns The dialog mode for that month.
 */
export function dialogModeFor(expectation: LeaseExpectation): AccrualPeriodDialogMode {
  const { tone } = resolveSituation(expectation);
  if (tone === 'awaiting') return 'confirm';
  if (tone === 'open' || tone === 'overdue') return 'pay';
  return 'history';
}

/** Today as `YYYY-MM-DD` in the broker's own timezone, the date input's format. */
function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

/** Cents as `moneyMask`'s unmasked form ("2800.00"), never below zero. */
function centsToUnmasked(cents: number): string {
  return (Math.max(cents, 0) / 100).toFixed(2);
}

/** The backend's own words for a refusal, or null when it sent none. */
function apiErrorMessage(error: unknown): string | null {
  const body = (error as { body?: { error?: unknown } } | null)?.body;
  return typeof body?.error === 'string' ? body.error : null;
}

/**
 * One month of a lease, opened from its row: confirm the pro-rata, record
 * what arrived or forgive the rest, and undo a receipt recorded by mistake.
 *
 * Housien records rent; it never charges it. Nothing here issues a boleto
 * or moves money -- each action writes down a fact the agency already knows.
 */
export function LeaseAccrualPeriodDialog({
  expectation,
  actions,
  onClose,
}: LeaseAccrualPeriodDialogProps) {
  const { t } = useTranslation('features/leases');
  if (!expectation) return null;
  const mode = dialogModeFor(expectation);
  const period = formatAccrualPeriod(expectation.accrual_period);

  return (
    <AdaptiveFormShell
      open
      onClose={onClose}
      title={t(`accrualPeriods.dialog.title.${mode}`, { period })}
      density="compact"
    >
      <div className="flex flex-col gap-(--space-4)" data-testid="lease-accrual-dialog">
        <MonthSummary expectation={expectation} />
        {mode === 'confirm' && (
          <ConfirmForm expectation={expectation} actions={actions} onDone={onClose} />
        )}
        {mode === 'pay' && (
          /* A reversal changes what is still owed; remounting re-suggests it. */
          <PayForm key={expectation.balance_cents} expectation={expectation} actions={actions} onDone={onClose} />
        )}
        <ReceiptList expectation={expectation} actions={actions} />
      </div>
    </AdaptiveFormShell>
  );
}

/** Expected, received and still owed for the month, with its due date. */
function MonthSummary({ expectation }: { expectation: LeaseExpectation }) {
  const { t } = useTranslation('features/leases');
  const cells = [
    { key: 'expected', value: expectation.expected_cents },
    { key: 'received', value: expectation.received_cents },
    { key: 'balance', value: Math.max(expectation.balance_cents, 0) },
  ];

  return (
    <dl className="grid grid-cols-3 gap-(--space-3) rounded-(--radius-md) bg-(--surface-secondary) px-(--space-4) py-(--space-3)">
      {cells.map((cell) => (
        <div key={cell.key} className="flex flex-col gap-(--space-0-5)">
          <dt className="text-(length:--text-meta) text-(--text-tertiary)">
            {t(`accrualPeriods.dialog.summary.${cell.key}`)}
          </dt>
          <dd className="m-0 text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary) [font-variant-numeric:tabular-nums]">
            {formatCents(cell.value)}
          </dd>
        </div>
      ))}
      <Text component="span" className="col-span-3 text-(length:--text-meta) text-(--text-tertiary)">
        {t('accrualPeriods.dialog.summary.due', { date: formatIsoDate(expectation.due_date) })}
      </Text>
    </dl>
  );
}

interface FormProps {
  expectation: LeaseExpectation;
  actions: AccrualPeriodActions;
  onDone: () => void;
}

/**
 * Runs one write, keeping the dialog open with the backend's own reason
 * when it refuses.
 */
function useSubmit(onDone: () => void) {
  const { t } = useTranslation('features/leases');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (write: () => Promise<void>) => {
    setSubmitting(true);
    setError(null);
    try {
      await write();
      onDone();
    } catch (e) {
      setError(apiErrorMessage(e) ?? t('accrualPeriods.dialog.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, error, setError, run };
}

/** A required money field that reads keystrokes as cents (see `moneyInputMask`). */
function MoneyInput({
  value,
  onChange,
  label,
  testId,
}: {
  value: string;
  onChange: (unmasked: string) => void;
  label: string;
  testId: string;
}) {
  return (
    <TextInput
      label={label}
      required
      inputMode="numeric"
      data-testid={testId}
      value={value ? moneyMask(value).masked : ''}
      onChange={(e) => onChange(moneyInputMask(e.target.value).unmasked)}
    />
  );
}

/** Confirms a pro-rata month, optionally at a negotiated amount. */
function ConfirmForm({ expectation, actions, onDone }: FormProps) {
  const { t } = useTranslation('features/leases');
  const [amount, setAmount] = useState(centsToUnmasked(expectation.expected_cents));
  const { submitting, error, setError, run } = useSubmit(onDone);

  const submit = () => {
    const cents = moneyToCents(amount);
    if (cents <= 0) {
      setError(t('accrualPeriods.dialog.errors.amountRequired'));
      return;
    }
    void run(() => actions.confirm(expectation.accrual_period, cents));
  };

  return (
    <div className="flex flex-col gap-(--space-3)">
      <Text component="p" className="m-0 text-(length:--text-body-sm) text-(--text-secondary)">
        {t('accrualPeriods.dialog.confirmHint')}
      </Text>
      <MoneyInput
        value={amount}
        onChange={setAmount}
        label={t('accrualPeriods.dialog.amount')}
        testId="lease-accrual-confirm-amount"
      />
      <FormFooter
        error={error}
        submitting={submitting}
        onSubmit={submit}
        label={t('accrualPeriods.dialog.submit.confirm')}
      />
    </div>
  );
}

/** Switches between recording a payment and writing the rest off. */
function PayForm({ expectation, actions, onDone }: FormProps) {
  const { t } = useTranslation('features/leases');
  const [kind, setKind] = useState<PayKind>('payment');

  return (
    <div className="flex flex-col gap-(--space-3)">
      <SegmentedControl
        value={kind}
        onChange={(v) => setKind(v as PayKind)}
        items={[
          { value: 'payment', label: t('accrualPeriods.dialog.kind.payment') },
          { value: 'writeOff', label: t('accrualPeriods.dialog.kind.writeOff') },
        ]}
        size="md"
        selectionRole="radio"
        testId="lease-accrual-kind"
      />
      {kind === 'payment'
        ? <PaymentFields expectation={expectation} actions={actions} onDone={onDone} />
        : <WriteOffFields expectation={expectation} actions={actions} onDone={onDone} />}
    </div>
  );
}

/** Records money that arrived, prefilled with what is still owed and today. */
function PaymentFields({ expectation, actions, onDone }: FormProps) {
  const { t } = useTranslation('features/leases');
  const [amount, setAmount] = useState(centsToUnmasked(expectation.balance_cents));
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const { submitting, error, setError, run } = useSubmit(onDone);

  const submit = () => {
    const cents = moneyToCents(amount);
    if (cents <= 0 || !receivedOn) {
      setError(t('accrualPeriods.dialog.errors.amountRequired'));
      return;
    }
    void run(() => actions.recordReceipt(expectation.accrual_period, cents, receivedOn));
  };

  return (
    <>
      <MoneyInput
        value={amount}
        onChange={setAmount}
        label={t('accrualPeriods.dialog.amountReceived')}
        testId="lease-accrual-receipt-amount"
      />
      <DateLabelled
        id="lease-accrual-received-on"
        label={t('accrualPeriods.dialog.receivedOn')}
        value={receivedOn}
        onChange={setReceivedOn}
      />
      <Text component="p" className="m-0 text-(length:--text-meta) text-(--text-tertiary)">
        {t('accrualPeriods.dialog.paymentHint')}
      </Text>
      <FormFooter
        error={error}
        submitting={submitting}
        onSubmit={submit}
        label={t('accrualPeriods.dialog.submit.payment')}
      />
    </>
  );
}

/** Forgives what is still owed, with the date and an optional note. */
function WriteOffFields({ expectation, actions, onDone }: FormProps) {
  const { t } = useTranslation('features/leases');
  const [writeOffDate, setWriteOffDate] = useState(todayIso());
  const [notes, setNotes] = useState('');
  const { submitting, error, run } = useSubmit(onDone);

  const submit = () => {
    void run(() => actions.writeOff(expectation.accrual_period, writeOffDate, notes.trim()));
  };

  return (
    <>
      <Text component="p" className="m-0 text-(length:--text-body-sm) text-(--text-secondary)">
        {t('accrualPeriods.dialog.writeOffHint', {
          amount: formatCents(Math.max(expectation.balance_cents, 0)),
        })}
      </Text>
      <DateLabelled
        id="lease-accrual-write-off-date"
        label={t('accrualPeriods.dialog.writeOffDate')}
        value={writeOffDate}
        onChange={setWriteOffDate}
      />
      <TextInput
        label={t('accrualPeriods.dialog.notes')}
        placeholder={t('accrualPeriods.dialog.notesPlaceholder')}
        value={notes}
        maxLength={500}
        data-testid="lease-accrual-write-off-notes"
        onChange={(e) => setNotes(e.target.value)}
      />
      <FormFooter
        error={error}
        submitting={submitting}
        onSubmit={submit}
        label={t('accrualPeriods.dialog.submit.writeOff')}
      />
    </>
  );
}

/** A date input with its label above, as the dialog's other fields have. */
function DateLabelled({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-(--space-1)">
      <Text
        as="label"
        htmlFor={id}
        className="text-(length:--text-meta) [font-weight:var(--font-medium)] text-(--text-secondary)"
      >
        {label}
      </Text>
      <DateField id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/** The refusal, when there is one, above the submit button. */
function FormFooter({
  error,
  submitting,
  onSubmit,
  label,
}: {
  error: string | null;
  submitting: boolean;
  onSubmit: () => void;
  label: string;
}) {
  const { t } = useTranslation('features/leases');

  return (
    <div className="flex flex-col items-end gap-(--space-2)">
      {error && (
        <Text
          component="p"
          role="alert"
          className="m-0 self-stretch rounded-(--radius-md) bg-(--status-error-bg) px-(--space-3) py-(--space-2) text-(length:--text-meta) text-(--status-error-text)"
        >
          {error}
        </Text>
      )}
      <CreateLeaseSubmitButton
        isSubmitting={submitting}
        onSubmit={onSubmit}
        label={label}
        savingLabel={t('accrualPeriods.dialog.saving')}
        testId="lease-accrual-submit"
      />
    </div>
  );
}

/** The month's live receipts, each one reversible. Nothing when there are none. */
function ReceiptList({
  expectation,
  actions,
}: {
  expectation: LeaseExpectation;
  actions: AccrualPeriodActions;
}) {
  const { t } = useTranslation('features/leases');
  const receipts = expectation.receipts ?? [];
  if (receipts.length === 0) return null;

  return (
    <section className="flex flex-col gap-(--space-2)" data-testid="lease-accrual-receipts">
      <Text
        component="h3"
        className="m-0 text-(length:--text-body-sm) [font-weight:var(--font-semibold)] text-(--text-primary)"
      >
        {t('accrualPeriods.dialog.receiptsTitle')}
      </Text>
      <List className="m-0 flex list-none flex-col gap-(--space-1-5) p-0">
        {receipts.map((receipt) => (
          <ReceiptItem
            key={receipt.id}
            receipt={receipt}
            onReverse={() => actions.reverse(expectation.accrual_period, receipt.id)}
          />
        ))}
      </List>
    </section>
  );
}

/** One receipt; reversing takes a second click, since it reopens the month. */
function ReceiptItem({
  receipt,
  onReverse,
}: {
  receipt: LeaseReceipt;
  onReverse: () => Promise<void>;
}) {
  const { t } = useTranslation('features/leases');
  const [confirming, setConfirming] = useState(false);
  const { submitting, error, run } = useSubmit(() => setConfirming(false));
  const label = receipt.kind === 'write_off'
    ? t('accrualPeriods.dialog.receipt.writeOff', { date: formatIsoDate(receipt.received_on) })
    : t('accrualPeriods.dialog.receipt.payment', {
      amount: formatCents(receipt.amount_cents),
      date: formatIsoDate(receipt.received_on),
    });

  return (
    <List.Item className="flex flex-col gap-(--space-1) rounded-(--radius-md) border border-(--border-subtle) px-(--space-3) py-(--space-2)">
      <div className="flex items-center justify-between gap-(--space-3)">
        <Text component="span" className="text-(length:--text-body-sm) text-(--text-primary)">
          {label}
        </Text>
        <UnstyledButton
          type="button"
          disabled={submitting}
          data-testid={`lease-accrual-reverse-${receipt.id}`}
          className="text-(length:--text-meta) [font-weight:var(--font-semibold)] text-(--status-error-text) hover:underline disabled:opacity-60"
          onClick={() => (confirming ? void run(onReverse) : setConfirming(true))}
        >
          {confirming ? t('accrualPeriods.dialog.reverseConfirm') : t('accrualPeriods.dialog.reverse')}
        </UnstyledButton>
      </div>
      {confirming && !error && (
        <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
          {t('accrualPeriods.dialog.reverseHint')}
        </Text>
      )}
      {error && (
        <Text component="span" role="alert" className="text-(length:--text-meta) text-(--status-error-text)">
          {error}
        </Text>
      )}
    </List.Item>
  );
}

export default LeaseAccrualPeriodDialog;
